import type { RubricCriterion } from '@/hooks/use-evaluation-rubrics-admin'
import {
  SCORING_PRESETS,
  scaleTypeOf,
  STANDARD_ORDINAL_LEVELS,
  weightTotalForKind,
  type ScoringPresetKey,
} from '@/lib/rubric-publish-payload'

export type ScoringConfig = {
  structured_weight?: number
  semantic_weight?: number
  llm_threshold?: number
}

export function criteriaForKind(criteria: RubricCriterion[], kind: RubricCriterion['kind']) {
  return criteria.filter((row) => row.kind === kind)
}

export function dimensionWeightSegments(criteria: RubricCriterion[]) {
  return criteriaForKind(criteria, 'dimension').map((row) => ({
    key: row.key,
    label: row.label || row.key,
    weight: row.weight ?? 0,
  }))
}

export function dimensionWeightTotal(criteria: RubricCriterion[]) {
  return weightTotalForKind(criteria, 'dimension')
}

export function ladderLevelLabels(criterion: RubricCriterion): string[] {
  const scale = criterion.scale as { type?: string; levels?: Array<{ label?: string; key?: string }> } | undefined
  if (scale?.type === 'ordinal' && Array.isArray(scale.levels) && scale.levels.length > 0) {
    return scale.levels.map((level) => level.label || level.key || '').filter(Boolean)
  }
  if (scaleTypeOf(criterion) === 'ordinal') {
    return STANDARD_ORDINAL_LEVELS.map((level) => level.label)
  }
  return []
}

export function scoringPresetLabel(
  preset: ScoringPresetKey | 'custom',
  scoring: ScoringConfig,
): string {
  if (preset !== 'custom') {
    return SCORING_PRESETS[preset].label
  }
  const match = (
    Object.entries(SCORING_PRESETS) as Array<[ScoringPresetKey, typeof SCORING_PRESETS.balanced]>
  ).find(
    ([, p]) =>
      p.structured_weight === (scoring.structured_weight ?? 0.6)
      && p.semantic_weight === (scoring.semantic_weight ?? 0.4)
      && p.llm_threshold === (scoring.llm_threshold ?? 0.7),
  )
  return match ? match[1].label : 'Custom'
}

export function formatScoringSummary(scoring: ScoringConfig): string {
  const structured = Math.round((scoring.structured_weight ?? 0.6) * 100)
  const semantic = Math.round((scoring.semantic_weight ?? 0.4) * 100)
  const threshold = Math.round((scoring.llm_threshold ?? 0.7) * 100)
  return `${structured}% structured · ${semantic}% semantic · deep AI from ${threshold}%`
}
